import React, { useState, useMemo } from "react";
import sumBy from "lodash/sumBy";
import { useMutation } from "@apollo/client/react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

import StorageLocationAutocomplete from "../capture/RackLocations/StorageLocationAutocomplete/StorageLocationAutocomplete";
import DuplicateRackLocationsModal from "./DuplicateRackLocationsModal";
import { COPY_AND_DISABLE_ASSET_REPLAY_LOG_MUTATION } from "../../constants/graphql/mutations";
import { ASSET_REPLAY_LOGS_QUERY } from "../../constants/graphql/queries";

export default function CopyReplayLogModal({ open, onClose, replayLog, projectId }) {
  const getInitialRackLocations = () => {
    if (replayLog?.rackLocations && replayLog.rackLocations.length > 0) {
      return replayLog.rackLocations.map(loc => ({
        warehouse: loc.warehouse,
        warehouse_locations: loc.warehouse_locations || [],
        mobile_warehouse_location: loc.mobile_warehouse_location,
        sub_qty: loc.sub_qty || 0,
        allocated_quantity: loc.allocated_quantity || 0,
        shipment_id: loc.shipment_id || null,
      }));
    }
    return [
      {
        warehouse: null,
        warehouse_locations: [],
        mobile_warehouse_location: null,
        sub_qty: 0,
        allocated_quantity: 0,
        shipment_id: null,
      },
    ];
  };

  const initialRackLocations = useMemo(() => getInitialRackLocations(), [replayLog]);
  const [rackLocations, setRackLocations] = useState(initialRackLocations);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateIndex, setDuplicateIndex] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [copyAndDisable, { loading }] = useMutation(COPY_AND_DISABLE_ASSET_REPLAY_LOG_MUTATION, {
    refetchQueries: [
      {
        query: ASSET_REPLAY_LOGS_QUERY,
        variables: { assetId: replayLog.assetId },
      },
    ],
    onCompleted: data => {
      if (data?.copyAndDisableAssetReplayLog?.assetReplayLog) {
        const newReplayLogId = data.copyAndDisableAssetReplayLog.assetReplayLog.id;
        setSnackbar({
          open: true,
          message: "Replay log successfully copied and original disabled",
          severity: "success",
        });
        setTimeout(() => {
          onClose(newReplayLogId);
        }, 1500);
      } else if (data?.copyAndDisableAssetReplayLog?.errors) {
        const errorMessage =
          data.copyAndDisableAssetReplayLog.errors.map(e => e.message).join(", ") ||
          "Failed to copy replay log";
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      }
    },
    onError: error => {
      setSnackbar({
        open: true,
        message: error.message || "An error occurred",
        severity: "error",
      });
    },
  });

  const totalQty = useMemo(() => sumBy(rackLocations, loc => loc.sub_qty || 0), [rackLocations]);

  const handleLocationChange = (index, values) => {
    const newLocations = [...rackLocations];
    newLocations[index] = { ...newLocations[index], ...values };
    setRackLocations(newLocations);
  };

  const handleQuantityChange = (index, value) => {
    const newLocations = [...rackLocations];
    newLocations[index] = {
      ...newLocations[index],
      sub_qty: Number(value) || 0,
    };
    setRackLocations(newLocations);
  };

  const handleAddLocation = () => {
    setRackLocations([
      ...rackLocations,
      {
        warehouse: null,
        warehouse_locations: [],
        mobile_warehouse_location: null,
        sub_qty: 0,
        allocated_quantity: 0,
        shipment_id: null,
      },
    ]);
  };

  const handleRemoveLocation = index => {
    if (rackLocations.length > 1) {
      setRackLocations(rackLocations.filter((_, i) => i !== index));
    }
  };

  const handleOpenDuplicateModal = index => {
    setDuplicateIndex(index);
    setDuplicateModalOpen(true);
  };

  const handleCloseDuplicateModal = () => {
    setDuplicateModalOpen(false);
    setDuplicateIndex(null);
  };

  const handleDuplicateLocations = newLocations => {
    setRackLocations(newLocations);
  };

  const handleSubmit = () => {
    const formattedLocations = rackLocations.map(loc => {
      const formatted = {
        warehouse_id: loc.warehouse?.id || loc.warehouse_id,
        warehouse_location_ids: (loc.warehouse_locations || []).map(l => l.id).filter(Boolean),
        sub_qty: loc.sub_qty || 0,
        allocated_quantity: loc.allocated_quantity || 0,
      };

      if (loc.mobile_warehouse_location?.id) {
        formatted.mobile_warehouse_location_id = loc.mobile_warehouse_location.id;
      }

      if (loc.shipment_id) {
        formatted.shipment_id = loc.shipment_id;
      }

      return formatted;
    });

    copyAndDisable({
      variables: {
        id: replayLog.id,
        rackLocations: formattedLocations,
      },
    });
  };

  const hasAllRequiredFields = rackLocations.every(
    loc => loc.warehouse_locations?.length > 0 && loc.sub_qty > 0 && loc.warehouse?.id
  );

  const hasChanges = useMemo(() => {
    if (rackLocations.length !== initialRackLocations.length) {
      return true;
    }

    return rackLocations.some((loc, index) => {
      const initial = initialRackLocations[index];
      if (!initial) return true;

      const locLocationIds = (loc.warehouse_locations || []).map(l => l.id).sort().join(',');
      const initialLocationIds = (initial.warehouse_locations || []).map(l => l.id).sort().join(',');

      return (
        locLocationIds !== initialLocationIds ||
        loc.sub_qty !== initial.sub_qty ||
        loc.warehouse?.id !== initial.warehouse?.id ||
        loc.mobile_warehouse_location?.id !== initial.mobile_warehouse_location?.id ||
        loc.shipment_id !== initial.shipment_id
      );
    });
  }, [rackLocations, initialRackLocations]);

  const isValid = hasAllRequiredFields && hasChanges;

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: "100%",
            maxWidth: 700,
          },
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Typography
            align="center"
            color="textSecondary"
            sx={{ px: 2, fontWeight: 500, fontSize: 22 }}
          >
            Replay Log Correction
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ mb: 1 }}
            >
              Use this to correct historical data for high-water-mark billing. This does not change current storage locations
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ position: "absolute", right: 16, top: 16 }}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 2 }}>
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight={600} mb={2}>
              Corrected Storage Locations
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              {rackLocations.map((location, index) => (
                <Box key={index} display="flex" gap={1} alignItems="center">
                  <Box flex={1}>
                    <StorageLocationAutocomplete
                      index={index}
                      name={`location-${index}`}
                      value={location}
                      projectId={projectId}
                      onChange={values => handleLocationChange(index, values)}
                      disabled={loading}
                      loading={false}
                    />
                  </Box>
                  <TextField
                    label="Location QTY"
                    type="number"
                    size="large"
                    value={location.sub_qty || ""}
                    onChange={e => handleQuantityChange(index, e.target.value)}
                    inputProps={{ min: 0 }}
                    sx={{ width: 140 }}
                    InputLabelProps={{ shrink: true }}
                    disabled={loading}
                  />
                  <Tooltip title="Delete">
                    <span>
                      <IconButton
                        onClick={() => handleRemoveLocation(index)}
                        size="medium"
                        disabled={loading || rackLocations.length === 1}
                        sx={{ color: rackLocations.length === 1 ? "rgba(0, 0, 0, 0.26)" : "inherit" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Duplicate">
                    <span>
                      <IconButton
                        onClick={() => handleOpenDuplicateModal(index)}
                        size="medium"
                        disabled={loading}
                        color="primary"
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ))}
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddLocation}
              sx={{ mt: 2 }}
              disabled={loading}
            >
              Add Another Location
            </Button>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, flexDirection: "column", alignItems: "stretch", gap: 2 }}>
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!isValid || loading}
            >
              {loading ? "Processing..." : "Save Correction"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {duplicateModalOpen && (
        <DuplicateRackLocationsModal
          index={duplicateIndex}
          rackLocations={rackLocations}
          onClose={handleCloseDuplicateModal}
          onDuplicate={handleDuplicateLocations}
        />
      )}
    </>
  );
}
